import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SiteService } from "../../../core/services/site.service";
import { Countries } from "../../../core/constants/countries";
import { LogHelper } from "../../../core/helpers/log.helper";
import { AlertService } from "../../../shared/alert/alert.service";
import { TemplateService } from "../../../core/services/template.service";
import {
  AutosuggestDropdownInputComponent
} from "../../../shared/autosuggest-dropdown-input/autosuggest-dropdown-input.component";
import { InputAddressComponent } from "../../../shared/input-address/input-address.component";
import { Address } from "../../../shared/input-address/address.model";
import { SiteListItem } from "../../../core/models/site-list.model";
import { SelectOption } from "../../../core/models/select-option.model";
import { ApiConsumerSimple } from "../../../core/models/api-consumer-simple.model";
import { ApiConsumerService } from "../../../core/services/api-consumer.service";
import { TimezoneSelectComponent } from "../../../shared/timezone-select/timezone-select.component";
import { requiredNoWhitespaceValidator } from "../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-site-edit',
  templateUrl: './site-edit.component.html'
})
export class SiteEditComponent implements OnInit {
  @ViewChild('siteAddress') siteAddress: InputAddressComponent;
  @ViewChild('countrySelect') countrySelect: AutosuggestDropdownInputComponent;
  @ViewChild('timezoneSelect') timezoneSelect: TimezoneSelectComponent;
  @ViewChild('logoFileInput') logoFileInput: ElementRef<HTMLInputElement>;

  siteId: string;
  form: UntypedFormGroup;
  isFormProcessing = false;
  countryOptions: { value: string, text: string }[] = [];
  hideAddressInput = false;
  site: SiteListItem;
  siteAddressCountryDisabled = true;
  apiConsumerOptions: SelectOption[] = [];

  // Preview shown next to the file input. Populated from the SAS URL the server returns on load,
  // replaced with a client-side object URL when the user picks a new file, cleared when removed.
  logoPreviewUrl: string | null = null;

  // File is held in memory until Save is clicked. Uploading on selection would orphan blobs every
  // time a user picks a logo then cancels or picks a different one.
  private pendingLogoFile: File | null = null;

  // Set to true when the user hits "Remove logo" so Save sends an explicit clear (empty string)
  // rather than the null-means-leave-untouched default.
  private logoCleared = false;

  constructor(private readonly templateService: TemplateService, private readonly apiConsumerService: ApiConsumerService, private readonly activatedRoute: ActivatedRoute, private readonly siteService: SiteService,
    private readonly alertService: AlertService, private readonly router: Router) {
    for (const country of Countries.all()) {
      this.countryOptions.push({ value: country.code, text: country.name });
    }
  }

  ngOnInit() {
    this.templateService.showHeader();

    this.form = new UntypedFormGroup({
      name: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(100)]),
      address: new UntypedFormControl(''),
      country: new UntypedFormControl(''),
      irgSiteId: new UntypedFormControl(''),
      siteAddress: new UntypedFormControl(null, Validators.required),
      apiConsumerId: new UntypedFormControl(''),
      timezone: new UntypedFormControl('', Validators.required)
    });

    this.loadAndSetApiConsumerOptions();

    this.activatedRoute.params.subscribe(params => {
      this.siteId = params.id;
      this.siteService.retrieveSite(this.siteId).subscribe({
        next: site => {
          this.site = site;
          this.form.setValue({
            name: site.name,
            address: site.address,
            country: site.country,
            irgSiteId: site.irgSiteId,
            siteAddress: site.siteAddress,
            apiConsumerId: site.apiConsumerId || '',
            timezone: site.timezone
          });
          this.countrySelect.setSelectedOption(site.country);
          this.countrySelect.disable();

          this.form.get('address').disable();

          let siteAddress = new Address(site.siteAddress);
          this.siteAddress.setAddress(siteAddress);

          if (siteAddress.equals(site.address)) {
            this.form.get('address').disable();
            this.form.get('country').disable();
            this.hideAddressInput = true;
          }

          if (site.siteAddress && site.siteAddress.country) {
            this.siteAddressCountryDisabled = true;
          } else {
            this.siteAddressCountryDisabled = false;
          }

          this.timezoneSelect.setTimezone(site.timezone);
          this.logoPreviewUrl = site.logoUrl ?? null;
        },
        error: error => {
          LogHelper.log(error);
          this.alertService.showWarningAlert('There was a problem loading the site!');
        }
      });
    });
  }

  /**
   * Retrieves and sets the api consumer options to be used in the api consumer dropdown
   */
  loadAndSetApiConsumerOptions(): void {
    this.apiConsumerService.getAllApiConsumers().subscribe({
      next: (apiConsumers: any) => {
        // A site with no consumer is a global site, so 'none' has to be a selectable value and not
        // just the absence of one - otherwise an owned site could never be handed back to PGO.
        this.apiConsumerOptions = [new SelectOption('', 'None - global site')].concat(
          apiConsumers.map((apiConsumer: ApiConsumerSimple) => {
            const consumerName = apiConsumer.enabled ? apiConsumer.name : `${apiConsumer.name} (Temporarily disabled)`;
            return new SelectOption(apiConsumer.id, consumerName);
          }));
      }
    });
  }

  onFormSubmit() {
    if (this.form.valid) {
      this.isFormProcessing = true;

      // Upload the logo first if one was picked; only send the save request once we have the
      // returned filename. If no new file was picked, skip straight to the save.
      if (this.pendingLogoFile) {
        this.siteService.uploadSiteLogo(this.pendingLogoFile).subscribe({
          next: response => this.submitUpdate(response.logoFilename),
          error: error => {
            LogHelper.log(error);
            this.isFormProcessing = false;
            this.alertService.showWarningAlert('Unable to upload logo. Check the file and try again.');
          }
        });
      } else {
        // logoCleared → send empty string to clear; otherwise null leaves the existing DB value.
        this.submitUpdate(this.logoCleared ? '' : null);
      }
    }
  }

  private submitUpdate(logoFilename: string | null) {
    // Ensure the country field is set correctly when the form input might be disabled
    const siteAddress = this.form.get('siteAddress').value;
    if (this.siteAddressCountryDisabled) {
      if (siteAddress && this.site?.siteAddress?.country) {
        siteAddress.country = this.site.siteAddress.country;
      }
    }

    // '' is the 'None - global site' option; send null so the server clears the owner.
    const apiConsumerId = this.form.get('apiConsumerId').value || null;

    this.siteService.updateSite(
      this.siteId,
      this.form.get('name').value,
      this.form.get('address').value,
      this.form.get('country').value,
      this.form.get('irgSiteId').value,
      this.siteAddressCountryDisabled ? siteAddress : this.form.get('siteAddress').value,
      apiConsumerId,
      this.form.get('timezone').value,
      logoFilename
    ).subscribe({
      next: () => {
        this.alertService.clearAll();
        this.alertService.showSuccessAlert('Site Updated Successfully');
        this.router.navigate(['/site']);
      },
      error: error => {
        LogHelper.log(error);
        this.isFormProcessing = false;
        // The server rejects an API consumer change that would break an existing trial assignment,
        // and its message names the trial and the consumers that would be valid. Showing a generic
        // toast here would throw away the only thing that tells the user what to do next.
        this.alertService.showWarningAlert(error.error && error.error.title
          ? error.error.title
          : 'Unable to update site. Check the details you have entered and try again. If this keeps happening, contact IT support.');
      }
    });
  }

  onOpenFileBrowser(event: Event) {
    event.preventDefault();
    this.logoFileInput.nativeElement.click();
  }

  onLogoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Hold on to the file - the actual upload runs from onFormSubmit. Previewing from an object
    // URL keeps the UI responsive without hitting the network at all.
    this.pendingLogoFile = file;
    this.logoCleared = false;
    this.logoPreviewUrl = URL.createObjectURL(file);

    // Reset so re-picking the same file still fires (change) again.
    input.value = '';
  }

  onLogoCleared() {
    this.pendingLogoFile = null;
    this.logoCleared = true;
    this.logoPreviewUrl = null;
  }
}
